import { useQuery } from '@tanstack/react-query';
import { Routes, Route, NavLink } from 'react-router-dom';
import { reportsApi, usersApi } from '@/lib/apiClient';
import PortfolioWip from './PortfolioWip';

const BAND_COLOR: Record<string, string> = {
  strong:    '#027835', // ew-green
  good:      '#DB7A25', // ew-performance
  attention: '#B8321C', // ew-danger
};

// Reports shell: a sub-nav over the Project Readiness report + the Portfolio WIP
// roll-up. WIP has its OWN permission (financials.view_portfolio) — separate
// from the per-job Financials tab (financials.view) — so it can be granted to
// select managers / execs / accounting on its own.
export default function ReportsPage() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const showWip = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('financials.view_portfolio') ?? false);

  const linkStyle = ({ isActive }: { isActive: boolean }) => ({
    padding: '7px 2px', marginRight: 22, textDecoration: 'none',
    font: 'var(--ew-fw-semibold) 13px/1 var(--ew-font)',
    color: isActive ? 'var(--ew-green-700)' : 'var(--ew-n-500)',
    borderBottom: isActive ? '2px solid var(--ew-green)' : '2px solid transparent',
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--ew-n-200)', marginBottom: 'var(--ew-s-5)' }}>
        <NavLink to="/reports" end style={linkStyle}>Project Readiness</NavLink>
        {showWip && <NavLink to="/reports/wip" style={linkStyle}>Work-in-Progress</NavLink>}
      </div>

      <Routes>
        <Route index element={<ReadinessReport />} />
        {showWip && <Route path="wip" element={<PortfolioWip />} />}
      </Routes>
    </div>
  );
}

function ReadinessReport() {
  const { data, isLoading } = useQuery({ queryKey: ['kpi'], queryFn: () => reportsApi.kpi() });

  const handleExport = async () => {
    const blob = await reportsApi.exportCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'projects.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <p style={{ color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>Calculating KPIs…</p>;

  const kpi = data as {
    overallScore: number;
    scoreBand: string;
    checklistCompletePct: number;
    avgItemsComplete: number;
    projectsWithIssues: number;
    activeProjectCount: number;
  } | undefined;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--ew-s-5)' }}>
        <div>
          <h2 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-h3)/var(--ew-lh-snug) var(--ew-font)', color: 'var(--ew-dark)' }}>
            Project Readiness Report
          </h2>
          <p style={{ margin: '4px 0 0', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-n-500)' }}>
            Readiness for jobs scheduled to start within the next two weeks.
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleExport}>Export CSV</button>
      </div>

      {kpi && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-6)' }}>
          <KpiCard label="Overall Score" value={`${kpi.overallScore}%`} color={BAND_COLOR[kpi.scoreBand]} />
          <KpiCard label="Jobs Starting ≤ 2 Wks" value={String(kpi.activeProjectCount)} />
          <KpiCard label="Checklist at 100%"  value={`${kpi.checklistCompletePct}%`} />
          <KpiCard label="Avg Items Done"      value={`${kpi.avgItemsComplete} / 13`} />
          <KpiCard label="Projects w/ Issues" value={String(kpi.projectsWithIssues)} color={kpi.projectsWithIssues > 0 ? '#B8321C' : '#027835'} />
        </div>
      )}

      <p style={{ color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 'var(--ew-lh-body)' }}>
        Score = (Checklist % × 0.6) + (Mobilization status × 0.4). Active pool excludes archived,
        Construction, Complete, Canceled, and Schedule Only projects without a mobilization status set.
      </p>
    </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--ew-n-200)',
      borderRadius: 'var(--ew-r-md)',
      padding: 'var(--ew-s-4)',
      borderLeft: color ? `3px solid ${color}` : undefined,
    }}>
      <div style={{
        font: 'var(--ew-fw-medium) var(--ew-fs-meta)/1.2 var(--ew-font)',
        letterSpacing: 'var(--ew-ls-caption)',
        textTransform: 'uppercase',
        color: 'var(--ew-n-500)',
        marginBottom: 'var(--ew-s-2)',
      }}>
        {label}
      </div>
      <div style={{
        font: 'var(--ew-fw-semibold) 28px/1 var(--ew-font)',
        color: color ?? 'var(--ew-dark)',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.01em',
      }}>
        {value}
      </div>
    </div>
  );
}
